import styled from "@emotion/styled";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from "@mui/material";
import { Button } from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { useChurchStore } from "features/churches";
import { useDenominationStore } from "features/denominations";
import {
  employeeSchema,
  useCreateEmployee,
  useEmployeesStore
} from "features/hr";
import moment from "moment";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BsXSquare } from "react-icons/bs";
import { EmployeeModalSteps } from "../EmployeeModalSteps";
import { BankForm } from "../Forms/BankForm";
import { EducationBackgroundForm } from "../Forms/EducationBackgroundForm";
import { EmergencyContactForm } from "../Forms/EmergencyContactForm";
import { EmployerInfoForm } from "../Forms/EmployerInfoForm";
import { FamilyInfoForm } from "../Forms/FamilyInfoForm";
import { JobForm } from "../Forms/JobForm";
import { PersonalInfoForm } from "../Forms/PersonalInfoForm";

const CloseIcon = styled(BsXSquare)`
  color: #ffffff;
  height: 22px;
  width: 22px;
  font-weight: bold;
  margin-left: auto;
  cursor: pointer;
`;

export const AddEmployeeModal = ({ open, onClose }) => {
  const addEmployeeFormActiveStep = useEmployeesStore(
    state => state.addEmployeeFormActiveStep
  );
  const setAddEmployeeFormActiveStep = useEmployeesStore(
    state => state.setAddEmployeeFormActiveStep
  );
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const selectedDenomination = useDenominationStore(
    state => state.selectedDenomination
  );

  const { t } = useTranslation();

  const createEmployeeMutation = useCreateEmployee();

  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
    watch,
    trigger
  } = useForm({
    defaultValues: {
      employee_id: "",
      title: "",
      first_name: "",
      middle_name: "",
      last_name: "",
      gender: "",
      date_of_birth: null,
      nationality_id: "66",
      marital_status: "",
      employee_photo: null,
      phone_number: "",
      email: "",
      address: "",
      position: "",
      employment_date: null,
      employment_type: "",
      salary_bank_name: "",
      salary_bank_account_number: "",
      pf_bank_name: "",
      pf_bank_account_number: "",
      social_id_number: "",
      tin_number: "",
      gross_salary: "",
      overtime: "",
      transport_allowance: "",
      house_allowance: "",
      communication_allowance: "",
      bonus: "",
      tax: "",
      credit: "",
      pension: "",
      provident_fund: "",
      penalty: "",
      emergency_contact_title: "",
      emergency_contact_first_name: "",
      emergency_contact_last_name: "",
      emergency_contact_middle_name: "",
      relationship_to_employee: "",
      emergency_contact_phone: "",
      emergency_contact_address: "",
      has_children: false,
      children: [],
      education_background: [],
      has_experience: false,
      work_experience: [],
      employer_name: "",
      employer_location: "",
      employer_house_number: "",
      employer_phone_number: "",
      mother_name: "",
      date_married: "",
      spouse_name: "",
      spouse_phone_number: "",
      spouse_job_title: "",
      birth_country_id: "",
      birth_region_id: "",
      birth_zone_id: "",
      birth_city_id: "",
      birth_sub_city_id: "",
      birth_woreda_id: "",
      employee_doc: null
    },
    mode: "onBlur",
    resolver: yupResolver(employeeSchema)
  });

  // Define the fields that belong to each step
  const stepFields = {
    1: [
      "employer_name",
      "employer_location",
      "employer_house_number",
      "employer_phone_number"
    ],
    2: [
      "employee_id",
      "title",
      "first_name",
      "middle_name",
      "last_name",
      "gender",
      "date_of_birth",
      "nationality_id",
      "marital_status",
      "employee_photo",
      "phone_number",
      "email",
      "address",
      "mother_name",
      "birth_country_id",
      "birth_region_id",
      "birth_zone_id",
      "birth_city_id",
      "birth_sub_city_id",
      "birth_woreda_id"
    ],
    3: [
      "marital_status",
      "date_married",
      "spouse_name",
      "spouse_phone_number",
      "spouse_job_title",
      "has_children",
      "children"
    ],
    4: [
      "education_background",
      "has_experience",
      "work_experience",
      "employee_doc"
    ],
    5: [
      "position",
      "employment_date",
      "employment_type",
      "has_experience",
      "work_experience"
    ],
    6: [
      "salary_bank_name",
      "salary_bank_account_number",
      "pf_bank_name",
      "pf_bank_account_number",
      "social_id_number",
      "tin_number",
      "gross_salary",
      "overtime",
      "transport_allowance",
      "house_allowance",
      "communication_allowance",
      "bonus",
      "tax",
      "credit",
      "pension",
      "provident_fund",
      "penalty"
    ],
    7: [
      "emergency_contact_title",
      "emergency_contact_first_name",
      "emergency_contact_middle_name",
      "emergency_contact_last_name",
      "relationship_to_employee",
      "emergency_contact_phone",
      "emergency_contact_address"
    ]
  };

  // Find the step with validation errors
  const findStepWithErrors = () => {
    if (!errors || Object.keys(errors).length === 0) return null;

    for (const [step, fields] of Object.entries(stepFields)) {
      const hasError = fields.some(field => errors[field]);
      if (hasError) {
        return parseInt(step);
      }
    }
    return null;
  };

  // Handle next button click
  const handleNext = async () => {
    // Validate current step fields
    const currentStepFields = stepFields[addEmployeeFormActiveStep];
    const isValid = await trigger(currentStepFields);

    if (isValid) {
      setAddEmployeeFormActiveStep(addEmployeeFormActiveStep + 1);
    }
  };

  const forms = {
    1: (
      <EmployerInfoForm
        register={register}
        errors={errors}
        control={control}
        denominationId={
          window.location.pathname.includes("denomination")
            ? selectedDenomination?.id
            : undefined
        }
        watch={watch}
        setValue={setValue}
      />
    ),
    2: (
      <PersonalInfoForm
        register={register}
        errors={errors}
        control={control}
        setValue={setValue}
        watch={watch}
      />
    ),
    3: (
      <FamilyInfoForm
        register={register}
        errors={errors}
        control={control}
        watch={watch}
        setValue={setValue}
      />
    ),
    4: (
      <EducationBackgroundForm
        register={register}
        errors={errors}
        control={control}
        watch={watch}
        setValue={setValue}
      />
    ),
    5: (
      <JobForm
        register={register}
        errors={errors}
        control={control}
        watch={watch}
        setValue={setValue}
      />
    ),
    6: <BankForm register={register} errors={errors} watch={watch} />,
    7: (
      <EmergencyContactForm
        register={register}
        errors={errors}
        control={control}
      />
    )
  };

  const onSubmit = values => {
    const formData = new FormData();

    // Helper function to format date to ISO format (YYYY-MM-DD)
    const formatDate = date => {
      if (!date) return "";
      return moment(date).format("YYYY-MM-DD");
    };

    // Filter out null/undefined values and convert to FormData
    Object.entries(values)
      .filter(([, value]) => value != null)
      .forEach(([key, value]) => {
        if ((key === "employee_photo" || key === "employee_doc") && value) {
          // Only append file if it has files
          if (value instanceof FileList && value.length > 0) {
            formData.append(key, value[0]);
          } else if (value instanceof File) {
            formData.append(key, value);
          }
        } else if (key === "has_children" || key === "has_experience") {
          formData.append(key, value ? "1" : "0");
        } else if (Array.isArray(value)) {
          // Handle arrays by appending each item with array notation
          value.forEach((item, index) => {
            Object.entries(item).forEach(([itemKey, itemValue]) => {
              if (itemValue != null && itemValue !== "") {
                formData.append(`${key}[${index}][${itemKey}]`, itemValue);
              }
            });
          });
        } else if (value instanceof Date) {
          formData.append(key, formatDate(value));
        } else if (value !== "") {
          formData.append(key, value);
        }
      });

    createEmployeeMutation.mutate(
      {
        churchId: selectedChurch?.id,
        employeeData: formData,
        isDenomination: window.location.pathname.includes("denomination"),
        denominationId: selectedDenomination?.id
      },
      {
        onSuccess: () => {
          reset();
          setAddEmployeeFormActiveStep(1);
          onClose();
        }
      }
    );
  };

  // Wrapper for handleSubmit that checks for errors
  const handleFormSubmit = handleSubmit(data => {
    const stepWithErrors = findStepWithErrors();
    if (stepWithErrors) {
      // Navigate to the step with errors
      setAddEmployeeFormActiveStep(stepWithErrors);
    } else {
      // If no errors, proceed with form submission
      onSubmit(data);
    }
  });

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={() => {
        setAddEmployeeFormActiveStep(1);
        onClose();
      }}
    >
      <DialogTitle sx={{ bgcolor: "primary.main" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="subtitle1" color="primary.contrastText">
            {t("HR.AddEmployee.EmployeeRegistration")}
          </Typography>
          <CloseIcon onClick={onClose} />
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack py={1} spacing={2}>
          <EmployeeModalSteps />
          {forms[addEmployeeFormActiveStep]}
          <MutationError error={createEmployeeMutation.error} sx={{ mt: 2 }} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          flavor="text"
          onClick={onClose}
          disabled={createEmployeeMutation.isLoading}
        >
          {t("form.common.cancel")}
        </Button>
        {addEmployeeFormActiveStep !== 1 && (
          <Button
            flavor="outlined"
            onClick={() =>
              setAddEmployeeFormActiveStep(addEmployeeFormActiveStep - 1)
            }
            disabled={createEmployeeMutation.isLoading}
          >
            {t("form.common.back")}
          </Button>
        )}
        {addEmployeeFormActiveStep !== 7 && (
          <Button onClick={handleNext}>{t("form.common.next")}</Button>
        )}
        {addEmployeeFormActiveStep === 7 && (
          <Button
            onClick={handleFormSubmit}
            disabled={createEmployeeMutation.isLoading}
          >
            {t("form.common.submit")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
